import { env } from '../config/env'
import { ApiError } from '../data/errors'
import { expireAuthSession, SESSION_EXPIRED_MESSAGE } from './session'

export interface ProviderOption {
  id: string
  name: string
  slug?: string
}

export interface LocationOption {
  id: string
  name: string
  address?: string
  minutesToSemaphoreYellow?: number
  minutesToSemaphoreRed?: number
}

export interface ProfileInfo {
  name?: string
  email?: string
  /** papel do usuário por providerId — ex.: { "3390": "owner" } (vem de user.providerRoles) */
  providerRoles?: Record<string, string>
}

const authHeaders = (token: string): HeadersInit => ({
  Accept: 'application/json',
  Authorization: `Bearer ${token}`,
})

const apiPath = (path: string): string => `${env.apiBase}${path}`

const asArray = <T>(payload: unknown, keys: string[]): T[] => {
  if (Array.isArray(payload)) {return payload as T[]}
  if (payload && typeof payload === 'object') {
    for (const key of keys) {
      const value = (payload as Record<string, unknown>)[key]
      if (Array.isArray(value)) {return value as T[]}
    }
  }
  return []
}

const textOr = (...values: unknown[]): string =>
  values.find((value) => typeof value === 'string' && value.trim() !== '') as string | undefined ?? ''

const idOf = (...values: unknown[]): string =>
  String(values.find((value) => value !== undefined && value !== null && value !== '') ?? '')

// user.providerRoles vem como { "3390": "owner", ... }; como fallback, parseamos user.roles
// no formato "<providerId>_<role>" (ex.: "3390_owner"), usado pelo mesmo endpoint.
const providerRolesOf = (user: Record<string, unknown>): Record<string, string> | undefined => {
  const raw = user.providerRoles
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const entries = Object.entries(raw as Record<string, unknown>).filter(
      (entry): entry is [string, string] => typeof entry[1] === 'string',
    )
    if (entries.length) {return Object.fromEntries(entries)}
  }
  const parsed: Record<string, string> = {}
  for (const value of asArray<unknown>(user, ['roles'])) {
    if (typeof value !== 'string') {continue}
    const match = value.match(/^(\d+)_(.+)$/)
    if (match) {parsed[match[1]] = match[2]}
  }
  return Object.keys(parsed).length ? parsed : undefined
}

const locationLabel = (location: Record<string, unknown>, id: string): Pick<LocationOption, 'name' | 'address'> => {
  const name = textOr(location.name, location.nome, location.description)
  const address = textOr(location.address)
  return { name: name || address || `Unidade ${id}`, address: address || undefined }
}

async function readJson(response: Response, token: string): Promise<unknown> {
  if (response.status === 401) {
    expireAuthSession(token)
    throw new ApiError(401, 'UNAUTHORIZED', SESSION_EXPIRED_MESSAGE)
  }
  const raw = await response.text()
  if (!response.ok) {
    try {
      const parsed = JSON.parse(raw) as { message?: string; error?: string }
      throw new Error(parsed.message ?? parsed.error ?? `HTTP ${response.status}`)
    } catch (error) {
      if (error instanceof Error && error.message !== raw) {throw error}
      throw Object.assign(new Error(raw || `HTTP ${response.status}`), { cause: error })
    }
  }
  return raw ? JSON.parse(raw) : null
}

export async function listProviders(token: string): Promise<ProviderOption[]> {
  const response = await fetch(apiPath('/api/me/providers'), { headers: authHeaders(token) })
  const payload = await readJson(response, token)
  return asArray<Record<string, unknown>>(payload, ['providers', 'items', 'data', 'result'])
    .map((provider) => ({
      id: idOf(provider.id, provider.providerId, provider.Id),
      name: textOr(provider.name, provider.tradingName, provider.companyName, provider.fantasyName, provider.slug) || `Provider ${idOf(provider.id, provider.providerId, provider.Id)}`,
      slug: textOr(provider.slug),
    }))
    .filter((provider) => provider.id)
}

export async function getProfile(token: string): Promise<ProfileInfo> {
  const response = await fetch(apiPath('/api/me/profile'), { headers: authHeaders(token) })
  const payload = await readJson(response, token)
  const user = payload && typeof payload === 'object' && 'user' in payload
    ? (payload as { user?: Record<string, unknown> }).user ?? {}
    : (payload as Record<string, unknown> | null) ?? {}
  return {
    name: textOr(user.firstName, user.name, user.fullName, user.displayName, user.userName, user.username, user.email),
    email: textOr(user.email, user.userName, user.username),
    providerRoles: providerRolesOf(user),
  }
}

export async function listLocations(token: string, providerId: string): Promise<LocationOption[]> {
  const response = await fetch(apiPath(`/api/providers/${providerId}/dropdownlocations`), { headers: authHeaders(token) })
  const payload = await readJson(response, token)
  return asArray<Record<string, unknown>>(payload, ['locations', 'items', 'data', 'result'])
    .map((location) => {
      const id = idOf(location.id, location.locationId, location.Id)
      const yellow = Number(location.minutesToSemaphoreYellow ?? location.MinutesToSemaphoreYellow)
      const red = Number(location.minutesToSemaphoreRed ?? location.MinutesToSemaphoreRed)
      return {
        id, ...locationLabel(location, id),
        minutesToSemaphoreYellow: Number.isFinite(yellow) && yellow > 0 ? yellow : undefined,
        minutesToSemaphoreRed: Number.isFinite(red) && red > yellow ? red : undefined,
      }
    })
    .filter((location) => location.id)
}
